import { useGameState } from '../../../net/GameProvider.js';
import { useI18n } from '../../../i18n.js';
import { Icon } from '../../Icon.js';

export function PauseOverlay() {
  const { state, actions } = useGameState();
  const { lang } = useI18n();
  const isMn = lang === 'mn';
  const { game } = state;

  if (!game) return null;

  const pause = game.pause ?? null;
  const isPaused = pause?.status === 'paused';
  const isRequestingPause = pause?.status === 'requesting';
  const isHost = Boolean(state.lobby?.hostId && (state.lobby.hostId === state.playerId || state.lobby.hostId === game.myPlayerId));

  const handleReturnToLobby = () => {
    const confirmReturn = window.confirm(
      isMn
        ? 'Тоглоомыг зогсоож, өрөө рүүгээ буцах уу? Бүх тоглогчид өрөөнд үлдэх бөгөөд онооны хязгаар гэх мэт тохиргоог өөрчлөх боломжтой болно.'
        : 'Return to lobby to change settings? All players will stay in the room and you can adjust the point limit and other settings.'
    );
    if (confirmReturn) {
      actions.returnToLobby();
    }
  };

  if (!pause) return null;

  return (
    <>
      {/* Pause Request Banner (when waiting for players to agree to pause) */}
      {isRequestingPause && (
        <div
          role="region"
          aria-label={isMn ? 'Тоглоом түр зогсоох санал' : 'Pause game request'}
          style={{
            position: 'absolute',
            top: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 80,
            background: 'rgba(16, 27, 22, 0.94)',
            border: '1px solid rgba(251, 191, 36, 0.65)',
            borderRadius: 12,
            padding: '10px 14px',
            width: 'min(420px, 92vw)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.55)',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, color: '#fde68a', fontSize: '0.9em' }}>
              ⏸️ {isMn
                ? `${pause.requestedByName} тоглоомыг түр зогсоох санал гаргалаа`
                : `${pause.requestedByName} requested to pause the game`}
            </span>
            <span style={{ fontSize: '0.78em', color: '#fbbf24', background: 'rgba(251, 191, 36, 0.15)', padding: '2px 8px', borderRadius: 999 }}>
              {isMn
                ? `⏳ Цаг удааширсан · ${pause.pauseAgreedPlayerIds.length}/${pause.requiredPlayerIds.length}`
                : `⏳ Timer slowed · ${pause.pauseAgreedPlayerIds.length}/${pause.requiredPlayerIds.length}`}
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {pause.requiredPlayerIds.map((pid) => {
              const pName = game.players.find((p) => p.playerId === pid)?.name ?? pid;
              const agreed = pause.pauseAgreedPlayerIds.includes(pid);
              return (
                <span
                  key={pid}
                  style={{
                    fontSize: '0.78em',
                    padding: '2px 8px',
                    borderRadius: 6,
                    background: agreed ? 'rgba(74, 222, 128, 0.18)' : 'rgba(255, 255, 255, 0.08)',
                    color: agreed ? '#86efac' : '#cbd5e1',
                    border: agreed ? '1px solid rgba(74, 222, 128, 0.4)' : '1px solid rgba(255, 255, 255, 0.15)',
                  }}
                >
                  {agreed ? '✓ ' : '⏳ '}{pName}
                </span>
              );
            })}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
            {isHost && (
              <button
                type="button"
                onClick={handleReturnToLobby}
                style={{
                  background: 'rgba(245, 158, 11, 0.2)',
                  color: '#fde68a',
                  border: '1px solid rgba(245, 158, 11, 0.45)',
                  padding: '5px 10px',
                  borderRadius: 6,
                  fontSize: '0.82em',
                  cursor: 'pointer',
                  fontWeight: 600,
                  marginRight: 'auto',
                }}
              >
                ⚙️ {isMn ? 'Өрөө рүү буцах' : 'Return to Lobby'}
              </button>
            )}
            {!pause.pauseAgreedPlayerIds.includes(game.myPlayerId) ? (
              <>
                <button
                  type="button"
                  onClick={() => actions.pauseAction('decline_pause')}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#e2e8f0',
                    border: '1px solid rgba(255, 255, 255, 0.25)',
                    padding: '5px 12px',
                    borderRadius: 6,
                    fontSize: '0.85em',
                  }}
                >
                  {isMn ? 'Татгалзах' : 'Decline'}
                </button>
                <button
                  type="button"
                  onClick={() => actions.pauseAction('agree_pause')}
                  style={{
                    background: 'var(--accent-color)',
                    color: '#000',
                    fontWeight: 700,
                    padding: '5px 12px',
                    borderRadius: 6,
                    fontSize: '0.85em',
                  }}
                >
                  {isMn ? '⏸️ Зөвшөөрөх' : '⏸️ Agree to Pause'}
                </button>
              </>
            ) : (
              <>
                <span style={{ fontSize: '0.8em', color: '#94a3b8', marginRight: 'auto' }}>
                  {isMn ? 'Бусад тоглогчдыг хүлээж байна...' : 'Waiting for other players to agree...'}
                </span>
                <button
                  type="button"
                  onClick={() => actions.pauseAction('decline_pause')}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#e2e8f0',
                    border: '1px solid rgba(255, 255, 255, 0.25)',
                    padding: '4px 10px',
                    borderRadius: 6,
                    fontSize: '0.8em',
                  }}
                >
                  {isMn ? 'Цуцлах' : 'Cancel'}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Full Paused Overlay (cards hidden, timer stopped until all agree to resume) */}
      {isPaused && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={isMn ? 'Тоглоом түр зогссон' : 'Game Paused'}
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 90,
            background: 'rgba(8, 15, 12, 0.85)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            style={{
              background: '#16251c',
              border: '1px solid rgba(214, 196, 138, 0.5)',
              borderRadius: 14,
              padding: '22px 20px',
              width: 'min(400px, 92vw)',
              boxShadow: '0 16px 40px rgba(0, 0, 0, 0.65)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: 14,
            }}
          >
            <div style={{ fontSize: '2em', lineHeight: 1 }}>⏸️</div>
            <div>
              <h2 style={{ margin: '0 0 6px', fontSize: '1.25em', color: '#fde68a' }}>
                {isMn ? 'Тоглоом түр зогссон' : 'Game Paused'}
              </h2>
              <p style={{ margin: 0, fontSize: '0.86em', color: '#cbd5e1', lineHeight: 1.45 }}>
                {isMn
                  ? 'Хөзрүүд нуугдаж, ээлжийн цаг түр зогссон байна. Бүх тоглогчид зөвшөөрснөөр цаг шинээр эхэлж тоглоом үргэлжилнэ.'
                  : 'All cards are hidden and the turn timer is paused. Once all players agree to resume, the turn timer will start from the beginning.'}
              </p>
            </div>

            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ fontSize: '0.8em', color: '#94a3b8' }}>
                {isMn
                  ? `Үргэлжлүүлэхэд бэлэн (${pause.resumeAgreedPlayerIds.length}/${pause.requiredPlayerIds.length}):`
                  : `Ready to resume (${pause.resumeAgreedPlayerIds.length}/${pause.requiredPlayerIds.length}):`}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 6 }}>
                {pause.requiredPlayerIds.map((pid) => {
                  const pName = game.players.find((p) => p.playerId === pid)?.name ?? pid;
                  const ready = pause.resumeAgreedPlayerIds.includes(pid);
                  return (
                    <span
                      key={pid}
                      style={{
                        fontSize: '0.82em',
                        padding: '4px 10px',
                        borderRadius: 8,
                        background: ready ? 'rgba(74, 222, 128, 0.2)' : 'rgba(255, 255, 255, 0.08)',
                        color: ready ? '#86efac' : '#cbd5e1',
                        border: ready ? '1px solid rgba(74, 222, 128, 0.45)' : '1px solid rgba(255, 255, 255, 0.15)',
                      }}
                    >
                      {ready ? '✓ ' : '⏳ '}{pName}
                    </span>
                  );
                })}
              </div>
            </div>

            {!pause.resumeAgreedPlayerIds.includes(game.myPlayerId) ? (
              <button
                type="button"
                onClick={() => actions.pauseAction('agree_resume')}
                style={{
                  width: '100%',
                  background: 'var(--accent-color)',
                  color: '#000',
                  fontWeight: 700,
                  padding: '10px 16px',
                  borderRadius: 8,
                  fontSize: '0.95em',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
              >
                <Icon name="play" size={18} />
                <span>{isMn ? 'Үргэлжлүүлэх' : 'Agree to Resume'}</span>
              </button>
            ) : (
              <button
                type="button"
                disabled
                style={{
                  width: '100%',
                  background: 'rgba(74, 222, 128, 0.18)',
                  color: '#86efac',
                  border: '1px solid rgba(74, 222, 128, 0.4)',
                  fontWeight: 600,
                  padding: '10px 16px',
                  borderRadius: 8,
                  fontSize: '0.9em',
                }}
              >
                {isMn ? '✓ Та зөвшөөрсөн — Бусдыг хүлээж байна...' : '✓ Ready — Waiting for other players...'}
              </button>
            )}

            {isHost && (
              <button
                type="button"
                onClick={handleReturnToLobby}
                style={{
                  width: '100%',
                  background: 'rgba(245, 158, 11, 0.2)',
                  color: '#fde68a',
                  border: '1px solid rgba(245, 158, 11, 0.5)',
                  fontWeight: 600,
                  padding: '9px 16px',
                  borderRadius: 8,
                  fontSize: '0.9em',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  cursor: 'pointer',
                }}
              >
                <Icon name="sliders-horizontal" size={16} />
                <span>{isMn ? 'Өрөө рүү буцах (Тохиргоо засах)' : 'Return to Lobby (Change Settings)'}</span>
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
